"use client";
import cn from "classnames";
import { useEffect, useRef, useState } from "react";
import s from "./AccordionItem.module.scss";
import st from "@/components/pages/blog/ai-article/common.module.scss";
import IconAngleDown from "public/img/icons/sprite/angle-down.svg";
import { SpriteIcon } from "@/components/UI/SpriteIcon";
import { RichText } from "@/components/pages/blog/ai-article/components/RichText";

export type AccordionItemProps = {
  heading: React.ReactNode;
  children?: React.ReactNode;
  active?: boolean;
  onToggle?: (isOpen: boolean) => void;
  className?: string;
  microData?: boolean;
  content?: string;
};

export const AccordionItem = ({
  heading,
  children,
  active,
  onToggle,
  className,
  content,
  microData = false,
}: AccordionItemProps) => {
  const contentWrapper = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(!!active);

  const openItem = () => {
    if (!contentWrapper.current) return;
    contentWrapper.current.style.maxHeight = contentWrapper.current.scrollHeight + "px";
    setIsOpen(true);
  };

  const closeItem = () => {
    if (!contentWrapper.current) return;
    contentWrapper.current.style.maxHeight = "0";
    setIsOpen(false);
  };

  const toggleItem = () => {
    if (!isOpen) openItem();
    else closeItem();
  };

  useEffect(() => {
    isOpen && openItem();
    onToggle && onToggle(isOpen);
  }, [isOpen, onToggle]);

  const itemMicroData = microData
    ? {
        itemScope: true,
        itemProp: "mainEntity",
        itemType: "https://schema.org/Question",
      }
    : {};

  const contentMicroData = microData
    ? {
        itemScope: true,
        itemProp: "acceptedAnswer",
        itemType: "https://schema.org/Answer",
      }
    : {};

  const headingMicroData = microData
    ? {
        itemProp: "name",
      }
    : {};

  const textMicroData = microData
    ? {
        itemProp: "text",
      }
    : {};

  return (
    <div
      className={cn(
        st.cardGlassHover,
        s.accordionItem,
        {
          [s.open]: isOpen,
        },

        className
      )}
      {...itemMicroData}
    >
      <div className={cn(s.headWrapper)} onClick={toggleItem}>
        {typeof heading === "string" ? (
          <h3 className={cn(st.fBig, s.heading)} {...headingMicroData}>
            {heading}
          </h3>
        ) : (
          heading
        )}
        <button aria-label="expand / shrink">
          <SpriteIcon icon={IconAngleDown} />
        </button>
      </div>
      <div ref={contentWrapper} className={s.contentWrapper} {...contentMicroData}>
        {content ? (
          <div className={s.content} {...textMicroData}>
            <RichText content={content} />
          </div>
        ) : (
          <div className={s.content} {...textMicroData}>
            {children}
          </div>
        )}
      </div>
    </div>
  );
};
